import React, { JSX, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

import Button from '@/main/components/shared/button/button';
import { getTranslateByKey } from '@/main/services/utils';
import * as S from './styles';
import { createPortal } from 'react-dom';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiPaper-root': {
        padding: '0 1rem 1rem'
      }
    },
    actions: {
      padding: '1rem 0 0'
    },
    paper: {
      padding: '0'
    }
  })
);

interface IAsyncModalAlertProps {
  closeByOverlay?: boolean;
}

const createPromise = (): [
  Promise<boolean>,
  (value: boolean | PromiseLike<boolean>) => void
] => {
  let resolver: (value: boolean | PromiseLike<boolean>) => void;
  const promise = new Promise<boolean>(resolve => {
    resolver = resolve;
  });
  return [promise, resolver!];
};

const useAsyncModalAlert = (): [
  (text: string | JSX.Element) => Promise<boolean>,
  (props: IAsyncModalAlertProps) => JSX.Element
] => {
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [resolver, setResolver] = useState<
    ((value: boolean | PromiseLike<boolean>) => void) | null
  >(null);
  const [label, setLabel] = useState<string | JSX.Element>('');

  const getConfirmation = (text: string | JSX.Element): Promise<boolean> => {
    setLabel(text);
    setOpen(true);

    const [promise, resolve] = createPromise();

    setResolver(() => resolve);

    return promise;
  };

  function handleClickOk(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    setOpen(false);

    if (resolver) {
      resolver(true);
    }
  }

  const ModalAlertation = ({
    closeByOverlay = false
  }: IAsyncModalAlertProps): JSX.Element => {
    const handleClose = (
      event: {},
      reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
      if (closeByOverlay) {
        setOpen(false);
      }
    };

    return (
      <>
        {createPortal(
          <Dialog open={open} className={classes.root} onClose={handleClose}>
            <DialogContent className={classes.paper}>
              <S.Text>{label}</S.Text>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <S.AreaFooterButtons>
                <Button id="id-modal-info-btn-ok" onClick={handleClickOk}>
                  {getTranslateByKey('information.ok')}
                </Button>
              </S.AreaFooterButtons>
            </DialogActions>
          </Dialog>,
          document.body
        )}
      </>
    );
  };

  return [getConfirmation, ModalAlertation];
};

export default useAsyncModalAlert;
